import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { MigrationObject, MigrationSummary } from "../../API/Models";
import { client } from "../../Clients";
import { formatExactDate, formatRelativeTime } from "../../Helper/dates";
import { MigrationUploadModal } from "./MigrationUploadModal";
import { MigrationFilters } from "./MigrationFilters";
import {
	numberFormatter,
	PAGE_SIZE_OPTIONS,
	PRIMARY_SITE_BASE_URL,
	REFERENCE_SITE_BASE_URL,
	PRIMARY_SITE_LABEL,
	REFERENCE_SITE_LABEL,
	ensureRelativePath,
	buildSiteLink,
	statusBadgeClass,
	normalizeMigrationEntry,
	ExternalLinkIcon
} from "./utils";
import type { StatusFilter, RedirectFilter, SkipFilter, MigrationUploadObject } from "./utils";

/** Migration object management with CSV import, status filtering and redirect tracking. */
const Migration: React.FC = () => {
	const [summary, setSummary] = useState<MigrationSummary | null>(null);
	const [items, setItems] = useState<MigrationObject[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [redirectFilter, setRedirectFilter] = useState<RedirectFilter>("all");
	const [skipFilter, setSkipFilter] = useState<SkipFilter>("all");
	const [loadingSummary, setLoadingSummary] = useState(true);
	const [loadingList, setLoadingList] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<string | null>(null);
	const [refreshToken, setRefreshToken] = useState(0);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [uploadPreview, setUploadPreview] = useState<string | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploadParsed, setUploadParsed] = useState<Record<string, MigrationUploadObject> | null>(null);
	const [uploadInfo, setUploadInfo] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);
	const [skipMutatingId, setSkipMutatingId] = useState<string | null>(null);
	const [skipMutatingAction, setSkipMutatingAction] = useState<"skip" | "unskip" | null>(null);

	const appliedFilters = useMemo(() => {
		return {
			search: searchTerm.trim() || undefined,
			isSupported: statusFilter === "supported" ? true : undefined,
			isBroken: statusFilter === "broken" ? true : undefined,
			isRedirected: redirectFilter === "redirected" ? true : undefined,
			isSkipped: skipFilter === "all" ? undefined : skipFilter === "skipped"
		};
	}, [searchTerm, statusFilter, redirectFilter, skipFilter]);

	const fetchData = useCallback(async () => {
		setError(null);
		setLoadingSummary(true);
		setLoadingList(true);
		console.log("[Migration] Fetch requested", {
			filters: appliedFilters,
			page,
			pageSize,
			refreshToken
		});
		try {
			const listPromise = client.migrations.apiMigrationsSearchGet(
				appliedFilters.search,
				appliedFilters.isSupported,
				appliedFilters.isBroken,
				appliedFilters.isRedirected,
				appliedFilters.isSkipped,
				page,
				pageSize
			);
			const summaryPromise = client.migrations.apiMigrationsSummaryGet();

			const [listResponse, summaryResponse] = await Promise.all([listPromise, summaryPromise]);
			const payload = listResponse.data;

			setItems(payload?.items ?? []);
			setTotal(payload?.total ?? 0);
			setSummary(summaryResponse.data ?? null);
			setLastUpdated(new Date().toISOString());
			console.log("[Migration] Fetch completed", {
				items: payload?.items?.length ?? 0,
				total: payload?.total ?? 0,
				summary: summaryResponse.data
			});
		} catch (caughtError) {
			console.error(caughtError);
			setError("Unable to load migrations at the moment. Please try again.");
		} finally {
			setLoadingSummary(false);
			setLoadingList(false);
		}
	}, [appliedFilters, page, pageSize, refreshToken]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const pageStart = items.length === 0 ? 0 : (page - 1) * pageSize + 1;
	const pageEnd = items.length === 0 ? 0 : (page - 1) * pageSize + items.length;

	const handleSearch = () => {
		setSearchTerm(searchInput.trim());
		setPage(1);
	};

	const clearFilters = () => {
		setSearchInput("");
		setSearchTerm("");
		setStatusFilter("all");
		setRedirectFilter("all");
		setSkipFilter("all");
		setPage(1);
	};

	const openUploadModal = () => {
		setUploadModalOpen(true);
	};

	const closeUploadModal = () => {
		setUploadModalOpen(false);
		setUploadFile(null);
		setUploadPreview(null);
		setUploadError(null);
		setUploadParsed(null);
		setUploadInfo(null);
		setUploading(false);
	};

	const handleJsonSelect = (file: File | null) => {
		setUploadFile(file);
		setUploadPreview(null);
		setUploadError(null);
		setUploadParsed(null);
		setUploadInfo(null);
		if (!file) {
			return;
		}
		if (!file.name.toLowerCase().endsWith(".json")) {
			setUploadError("Only .json files are supported.");
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			try {
				const text = reader.result ?? "";
				const parsed = JSON.parse(text as string);
				if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
					setUploadPreview(null);
					setUploadParsed(null);
					setUploadError("Unexpected JSON format. Expected an object with URLs as keys.");
					return;
				}
				const normalized: Record<string, MigrationUploadObject> = {};
				for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
					if (!value || typeof value !== "object" || Array.isArray(value)) {
						setUploadPreview(null);
						setUploadParsed(null);
						setUploadError(`Entry '${key}' is not a valid object.`);
						return;
					}
					normalized[key] = normalizeMigrationEntry(value as Record<string, unknown>);
				}
				setUploadPreview(JSON.stringify(parsed, null, 2));
				setUploadParsed(normalized);
				console.log("[Migration] Upload parsed", {
					fileName: file.name,
					entryCount: Object.keys(normalized).length
				});
			} catch (parseError) {
				console.error(parseError);
				setUploadPreview(null);
				setUploadError("Unable to parse the selected JSON file.");
				setUploadParsed(null);
			}
		};
		reader.onerror = () => {
			setUploadPreview(null);
			setUploadError("Failed to read the selected file.");
			setUploadParsed(null);
		};
		reader.readAsText(file, "utf-8");
	};

	const handleImportJson = async () => {
		if (!uploadParsed) {
			return;
		}
		console.log("[Migration] Import started", { entryCount: Object.keys(uploadParsed).length });
		setUploading(true);
		setUploadError(null);
		setUploadInfo(null);
		try {
			const response = await client.migrations.apiMigrationsImportPost(uploadParsed);
			const summary = response.data;
			setUploadInfo(
				`Imported successfully: added ${summary?.added ?? 0}, updated ${summary?.updated ?? 0}, skipped ${summary?.skipped ?? 0}.`
			);
			console.log("[Migration] Import completed", summary);
			await fetchData();
		} catch (caughtError) {
			console.error(caughtError);
			setUploadError("Unable to import the provided JSON. Please verify the structure and try again.");
		} finally {
			setUploading(false);
		}
	};

	const handleSkipToggle = async (id: string | undefined | null, shouldSkip: boolean) => {
		if (!id) {
			return;
		}
		setSkipMutatingId(id);
		setSkipMutatingAction(shouldSkip ? "skip" : "unskip");
		setError(null);
		try {
			if (shouldSkip) {
				await client.migrations.apiMigrationsIdSkipPost(id);
			} else {
				await client.migrations.apiMigrationsIdUnskipPost(id);
			}
			await fetchData();
		} catch (caughtError) {
			console.error(caughtError);
			setError("Unable to update the skip state. Please try again.");
		} finally {
			setSkipMutatingId(null);
			setSkipMutatingAction(null);
		}
	};

	const summaryCards = useMemo(() => {
		if (!summary) return [];
		const ratioPercent = Math.round((summary.supportRatio ?? 0) * 1000) / 10;

		return [
			{ label: "Total migrations", value: numberFormatter.format(summary.total ?? 0) },
			{ label: "Supported", value: numberFormatter.format(summary.supported ?? 0) },
			{ label: "Broken", value: numberFormatter.format(summary.broken ?? 0) },
			{ label: "Redirected", value: numberFormatter.format(summary.redirected ?? 0) },
			{ label: "Support ratio", value: `${ratioPercent.toFixed(1)} %` }
		];
	}, [summary]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Migration</p>
						<h1 className="text-2xl font-semibold text-slate-900">Content migration monitor</h1>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Last refreshed {formatRelativeTime(lastUpdated)} • {formatExactDate(lastUpdated)}
							</p>
						)}
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<div className="flex flex-col gap-1 text-xs">
							<label className="font-semibold uppercase tracking-wide text-slate-500">Reference site</label>
							<input
								type="text"
								readOnly
								value={REFERENCE_SITE_BASE_URL}
								className="w-72 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-700"
							/>
						</div>
						<button
							type="button"
							onClick={openUploadModal}
							className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
						>
							Upload JSON
						</button>
						<button
							type="button"
							onClick={() => setRefreshToken((token) => token + 1)}
							className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
						>
							{loadingList || loadingSummary ? (
								<>
									<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
									Refreshing
								</>
							) : (
								"Refresh"
							)}
						</button>
					</div>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
					<header className="mb-4 flex items-center justify-between">
						<p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Migration metrics</p>
						{loadingSummary && <span className="text-xs font-medium text-slate-400">Loading...</span>}
					</header>
					{loadingSummary ? (
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
							{Array.from({ length: 5 }).map((_, index) => (
								<div key={`summary-skeleton-${index}`} className="h-32 animate-pulse rounded-2xl border border-slate-100 bg-slate-50" />
							))}
						</div>
					) : (
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
							{summaryCards.map((card) => (
								<div key={card.label} className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</p>
									<p className="mt-3 text-2xl font-bold text-slate-900">{card.value}</p>
									<p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">Current snapshot</p>
								</div>
							))}
						</div>
					)}
				</section>

				<MigrationFilters
					searchInput={searchInput}
					onSearchInputChange={setSearchInput}
					statusFilter={statusFilter}
					onStatusFilterChange={(value) => { setStatusFilter(value); setPage(1); }}
					redirectFilter={redirectFilter}
					onRedirectFilterChange={(value) => { setRedirectFilter(value); setPage(1); }}
					skipFilter={skipFilter}
					onSkipFilterChange={(value) => { setSkipFilter(value); setPage(1); }}
					onApply={handleSearch}
					onClear={clearFilters}
				/>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Migration entries</p>
							<p className="text-sm text-slate-500">
								Showing {items.length === 0 ? 0 : pageStart}-{pageEnd} of {total} entries
							</p>
						</div>
						<div className="flex items-center gap-2 text-sm text-slate-600">
							Page size
							<select
								value={pageSize}
								onChange={(event) => {
									setPageSize(Number(event.target.value));
									setPage(1);
								}}
								className="rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none"
							>
								{PAGE_SIZE_OPTIONS.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
					</header>
					{error && <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
					{loadingList ? (
						<div className="px-5 py-10 text-center text-slate-500">Loading migrations...</div>
					) : items.length === 0 ? (
						<div className="px-5 py-10 text-center text-slate-500">No migrations match your filters.</div>
					) : (
						<div className="px-3 py-3">
							<table className="min-w-full table-fixed divide-y divide-slate-100 text-sm">
								<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="w-2/5 px-5 py-3">Page</th>
									<th className="w-1/5 px-5 py-3">Flags</th>
									<th className="w-1/6 px-5 py-3">Crawled</th>
									<th className="w-1/6 px-5 py-3">Assets</th>
									<th className="w-1/5 px-5 py-3 text-right">Links</th>
									<th className="w-[140px] px-5 py-3 text-right">Skip</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-100">
									{items.map((item) => {
										const relativePath = ensureRelativePath(item.url || item.requestedUrl || item.id);
										const primaryLink = buildSiteLink(PRIMARY_SITE_BASE_URL, relativePath);
										const referenceLink = buildSiteLink(REFERENCE_SITE_BASE_URL, relativePath);
										const displayPath = relativePath ?? item.url ?? item.requestedUrl ?? "\u2014";
										const pageAnchorHref = primaryLink ?? referenceLink ?? "#";
										const isSkipped = item.isSkipped ?? false;
										const skipBusy = skipMutatingId === item.id;
										const buttonLabel = skipBusy
											? skipMutatingAction === "skip"
												? "Skipping\u2026"
												: "Unskipping\u2026"
											: isSkipped
												? "Unskip"
												: "Skip";

										return (
											<tr key={item.id} className="align-center hover:bg-slate-50">
												<td className="break-words px-5 py-3">
													<div className="font-semibold text-slate-900">{item.pageTitle || item.contentHeading || "Untitled page"}</div>
													{displayPath === "\u2014" ? (
														<span className="mt-1 block text-xs text-slate-400">\u2014</span>
													) : (
														<a
															href={pageAnchorHref}
															target="_blank"
															rel="noreferrer"
															className="mt-1 block break-words text-xs text-slate-500 hover:text-slate-900"
														>
															{displayPath}
														</a>
													)}
												</td>
												<td className="px-5 py-4 align-top">
													<div className="flex flex-wrap gap-2 text-xs font-semibold">
														{item.isSupported && <span className={`rounded-full px-3 py-0.5 ${statusBadgeClass("supported")}`}>Supported</span>}
														{item.isBroken && <span className={`rounded-full px-3 py-0.5 ${statusBadgeClass("broken")}`}>Broken</span>}
														{item.isRedirected && <span className={`rounded-full px-3 py-0.5 ${statusBadgeClass("redirected")}`}>Redirected</span>}
														{isSkipped && <span className={`rounded-full px-3 py-0.5 ${statusBadgeClass("skipped")}`}>Skipped</span>}
														{!item.isSupported && !item.isBroken && !item.isRedirected && !isSkipped && (
															<span className="rounded-full bg-slate-100 px-3 py-0.5 text-xs font-semibold text-slate-500">Uncategorized</span>
														)}
													</div>
												</td>
												<td className="px-5 py-4 align-top text-slate-600">
													<div className="text-xs font-semibold text-slate-900">{item.crawledAt ? formatExactDate(item.crawledAt) : "\u2014"}</div>
													<div className="text-[11px] uppercase tracking-wide text-slate-400">{item.crawledAt ? formatRelativeTime(item.crawledAt) : ""}</div>
												</td>
												<td className="px-5 py-4 align-top text-slate-600">
													<div className="text-xs">{(item.documentLinks?.length ?? 0) + (item.downloadedDocuments?.length ?? 0)} linked files</div>
													<div className="text-[11px] uppercase tracking-wide text-slate-400">
														{item.documentLinks?.length ?? 0} document links \u2022 {item.downloadedDocuments?.length ?? 0} downloaded
													</div>
												</td>
												<td className="px-5 py-4 align-top">
													<div className="flex flex-wrap justify-end gap-2">
														{primaryLink && (
															<a
																href={primaryLink}
																target="_blank"
																rel="noreferrer"
																className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
																aria-label="Open on apa.cd"
																title={`Open on ${PRIMARY_SITE_BASE_URL}`}
															>
																<ExternalLinkIcon />
																<span>{PRIMARY_SITE_LABEL}</span>
															</a>
														)}
														{referenceLink && (
															<a
																href={referenceLink}
																target="_blank"
																rel="noreferrer"
																className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
																aria-label="Open on reference site"
																title={`Open on ${REFERENCE_SITE_BASE_URL}`}
															>
																<ExternalLinkIcon />
																<span>{REFERENCE_SITE_LABEL}</span>
															</a>
														)}
													</div>
												</td>
												<td className="px-5 py-4 align-top text-right">
													<button
														type="button"
														onClick={() => handleSkipToggle(item.id, !isSkipped)}
														disabled={skipBusy}
														className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
													>
														{buttonLabel}
													</button>
													{isSkipped && <p className="mt-2 text-[11px] text-slate-500">Skipped entries remain in the list but are excluded elsewhere.</p>}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
					<footer className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 px-5 py-4 text-sm text-slate-600">
						<div>
							Page {page} of {totalPages}
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((current) => Math.max(1, current - 1))}
								disabled={page === 1}
								className="rounded-full border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Previous
							</button>
							<button
								type="button"
								onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
								disabled={page === totalPages || items.length === 0}
								className="rounded-full border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Next
							</button>
						</div>
					</footer>
				</section>
			</div>

			<MigrationUploadModal
				show={uploadModalOpen}
				onHide={closeUploadModal}
				uploadFile={uploadFile}
				uploadPreview={uploadPreview}
				uploadError={uploadError}
				uploadInfo={uploadInfo}
				uploadParsed={uploadParsed}
				uploading={uploading}
				onFileSelect={handleJsonSelect}
				onImport={handleImportJson}
			/>
		</div>
	);
};

export default Migration;
